import React, { AnchorHTMLAttributes, useEffect, useRef, useState } from 'react';

import { Placement } from '@floating-ui/react';
import classNames from 'classnames';

import { useScrollStatus } from '@travauxlib/shared/src/hooks/useScrollStatus';

import { TableHeaderRow } from './TableHeaderRow';
import { TableRow } from './TableRow';
import { ColumnConfig, Item, RowAction, SizePaddingClassName } from './types';

import { ActionListProps } from '../ActionList';
export type { ColumnConfig } from './types';

type Props<T> = {
  className?: string;
  title?: string;
  size?: SizePaddingClassName;
  columnConfigs: ColumnConfig<T>[];
  items: Item<T>[];
  onRowClick?: (item: T) => {
    action?: () => void;
    to?: string;
    href?: AnchorHTMLAttributes<T>['href'];
    target?: AnchorHTMLAttributes<T>['target'];
  };
  rowActions?: (item: T) => RowAction[] | React.ReactElement;
  actionListProps?: Partial<ActionListProps<T>>;
  // TODO prevent the use of fixFirstColumn if an item has additionalRowContent prop
  fixedColumn?: 'first' | 'last';
  renderAdditionalRowContent?: (item: T) => React.ReactElement;
  itemsPerPage?: number;
  defaultSort?: { columnKey: keyof T; order: 'asc' | 'desc' };
  wrapLines?: boolean;
  inline?: boolean;
  autoResetPagination?: boolean;
  disableRow?: (item: T) => boolean;
  footer?: React.ReactElement;
  rowActionsPosition?: Placement;
  currentPage?: number;
  hasMultiplePages?: boolean;
  onSortChanged?: (columnName: keyof T) => void;
  shouldDisplayChevron?: (columnKey: keyof T, direction: 'asc' | 'desc') => boolean;
  paginationFooter?: React.ReactElement;
  helperFooter?: React.ReactElement;
  emptyState?: React.ReactElement;
};

export const BaseTable = <T extends object>({
  className,
  title,
  size = 'md',
  columnConfigs,
  items,
  onRowClick,
  disableRow,
  rowActions,
  actionListProps,
  fixedColumn,
  renderAdditionalRowContent,
  wrapLines,
  inline = false,
  footer,
  rowActionsPosition,
  currentPage,
  onSortChanged = () => {},
  shouldDisplayChevron = () => false,
  paginationFooter,
  helperFooter,
  emptyState,
}: Props<T>): React.ReactElement => {
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | undefined>(undefined);
  const contentRef = useRef<HTMLDivElement>(null);
  const { hasHorizontalScroll } = useScrollStatus(contentRef);

  const hasExpandableRows =
    items.some(item => item.additionalRowContent) || !!renderAdditionalRowContent;

  useEffect(() => {
    setExpandedRowIndex(undefined);
  }, [currentPage]);

  return (
    <div className={classNames('ctn-neutral-white relative', className)}>
      {!inline && title && <div className="text-h5 mb-md px-md">{title}</div>}
      <div
        className={classNames('overflow-x-auto rounded-t-[inherit]', {
          'mb-md': !!paginationFooter,
        })}
        ref={contentRef}
      >
        <table className="w-full border-separate border-spacing-0">
          {!inline && (
            <thead>
              <TableHeaderRow<T>
                size={size}
                columnConfigs={columnConfigs}
                // Pas de fixage de head pour la dernière colonne, ça décale tout quand le titre est vide
                fixedColumn={fixedColumn === 'first'}
                isHorizontallyScrollable={hasHorizontalScroll}
                onSortChanged={onSortChanged}
                shouldDisplayChevron={shouldDisplayChevron}
              />
            </thead>
          )}
          <>
            {items.length === 0 && emptyState ? (
              <tbody>
                <tr>
                  <td colSpan={columnConfigs.length}>
                    <div className="my-md">{emptyState}</div>
                  </td>
                </tr>
              </tbody>
            ) : (
              items.map((item, index) => (
                <TableRow<T>
                  disableRow={disableRow}
                  wrapLines={wrapLines}
                  key={`row-${index}`}
                  item={item}
                  rowIndex={index}
                  size={size}
                  additionalRowContent={item.additionalRowContent}
                  renderAdditionalRowContent={renderAdditionalRowContent}
                  onClickExpand={() =>
                    setExpandedRowIndex(expandedRowIndex =>
                      expandedRowIndex === index ? undefined : index,
                    )
                  }
                  isExpanded={expandedRowIndex === index}
                  columnConfigs={columnConfigs}
                  rowActions={rowActions}
                  fixedColumn={fixedColumn}
                  hasHorizontalScroll={hasHorizontalScroll}
                  hasExpandableRows={hasExpandableRows}
                  onRowClick={onRowClick}
                  inline={inline}
                  rowActionsPosition={rowActionsPosition}
                  actionListProps={actionListProps}
                />
              ))
            )}
          </>
          {footer && <tfoot className="[&_td]:py-sm">{footer}</tfoot>}
        </table>
      </div>
      {(helperFooter || paginationFooter) && (
        <div className="flex justify-between mx-md pb-md">
          {helperFooter && <div className="my-auto w-full">{helperFooter}</div>}
          {paginationFooter && <div className="my-auto w-full">{paginationFooter}</div>}
        </div>
      )}
    </div>
  );
};
